/**
 * Client HTTP compartilhado da IA-API (Bearer JWT do Supabase).
 * Usado por `supportCenterAgendaApi`, etc.
 */
import { supabase } from '../lib/supabase'

export async function getIaApiAccessToken(
  sessionExpiredMessage = 'Sessão expirada. Faça login novamente.'
): Promise<string> {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  const token = data.session?.access_token
  if (!token) {
    throw new Error(sessionExpiredMessage)
  }
  return token
}

function nestedErrorMessage(body: Record<string, unknown>): string | null {
  const nestedError =
    body.error && typeof body.error === 'object' && !Array.isArray(body.error)
      ? (body.error as Record<string, unknown>)
      : null

  const detailParts = collectValidationDetails(nestedError ?? body)
  const message =
    (typeof nestedError?.message === 'string' && nestedError.message) ||
    (typeof body.message === 'string' && body.message) ||
    (typeof body.error === 'string' && body.error) ||
    (typeof body.detail === 'string' && body.detail) ||
    null

  const base = message?.trim() || null
  if (base && detailParts.length > 0) {
    return `${base}: ${detailParts.join('; ')}`
  }
  if (detailParts.length > 0) return detailParts.join('; ')
  return base
}

function collectValidationDetails(source: Record<string, unknown>): string[] {
  const parts: string[] = []

  const pushIssue = (issue: unknown) => {
    if (!issue || typeof issue !== 'object' || Array.isArray(issue)) return
    const row = issue as Record<string, unknown>
    const pathRaw = row.path ?? row.field ?? row.property
    const path = Array.isArray(pathRaw)
      ? pathRaw.map(String).join('.')
      : typeof pathRaw === 'string'
        ? pathRaw
        : ''
    const msg =
      (typeof row.message === 'string' && row.message) ||
      (typeof row.msg === 'string' && row.msg) ||
      ''
    const text = [path, msg].filter(Boolean).join(': ').trim()
    if (text) parts.push(text)
  }

  const candidates = [source.details, source.errors, source.issues, nestedErrorDetails(source)]
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      candidate.forEach(pushIssue)
      if (typeof candidate[0] === 'string') {
        parts.push(...candidate.filter((item): item is string => typeof item === 'string'))
      }
    } else if (typeof candidate === 'string' && candidate.trim()) {
      parts.push(candidate.trim())
    } else if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      for (const [key, value] of Object.entries(candidate as Record<string, unknown>)) {
        if (typeof value === 'string') parts.push(`${key}: ${value}`)
        else if (Array.isArray(value)) {
          value.forEach((item) => {
            if (typeof item === 'string') parts.push(`${key}: ${item}`)
            else pushIssue(item)
          })
        }
      }
    }
  }

  return [...new Set(parts.filter(Boolean))]
}

function nestedErrorDetails(source: Record<string, unknown>): unknown {
  const nested =
    source.error && typeof source.error === 'object' && !Array.isArray(source.error)
      ? (source.error as Record<string, unknown>)
      : null
  return nested?.details ?? nested?.errors ?? nested?.issues ?? null
}

/** Erro HTTP da IA-API com status (ex.: 424 = pessoa não encontrada no CPF). */
export class IaApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'IaApiError'
    this.status = status
  }
}

export function parseIaApiErrorMessage(
  status: number,
  body: Record<string, unknown> | null,
  rawText: string,
  apiLabel = 'IA-API'
): string {
  const fromBody = body ? nestedErrorMessage(body) : null
  const fromText = rawText.trim()
  if (fromBody || fromText) return fromBody || fromText
  // Create/reagendar: pessoa ausente → 424 (Failed Dependency).
  if (status === 424) {
    return 'Pessoa não encontrada para o CPF informado.'
  }
  return `Não foi possível concluir na ${apiLabel} (${status}).`
}

export function assertIaApiBusinessSuccess(payload: unknown, apiLabel = 'IA-API'): void {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return
  const row = payload as Record<string, unknown>
  if (row.success === false || row.ok === false) {
    throw new Error(
      nestedErrorMessage(row) || `${apiLabel} não confirmou sucesso na operação.`
    )
  }
}

export function unwrapIaApiData(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload
  const row = payload as Record<string, unknown>
  return 'data' in row ? row.data : payload
}

export async function iaApiFetch<T>(
  baseUrl: string,
  path: string,
  init: RequestInit & { method?: string } = {},
  options?: {
    apiLabel?: string
    sessionExpiredMessage?: string
  }
): Promise<T> {
  const apiLabel = options?.apiLabel ?? 'IA-API'
  const token = await getIaApiAccessToken(options?.sessionExpiredMessage)
  const url = `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`

  const response = await fetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  })

  if (!response.ok) {
    const rawText = await response.text()
    let body: Record<string, unknown> | null = null
    try {
      body = rawText ? (JSON.parse(rawText) as Record<string, unknown>) : null
    } catch {
      body = null
    }
    throw new IaApiError(
      parseIaApiErrorMessage(response.status, body, rawText, apiLabel),
      response.status
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  if (!text.trim()) return undefined as T
  const parsed = JSON.parse(text) as unknown
  assertIaApiBusinessSuccess(parsed, apiLabel)
  return parsed as T
}
